import type { CrmTranslate } from './crm-copy';

export function createCrmSettingsCopy(t: CrmTranslate) {
  return {
    settingsPage: {
      documentTitle: t('crm.settingsPage.documentTitle'),
      header: {
        title: t('crm.settingsPage.header.title'),
        description: t('crm.settingsPage.header.description'),
      },
      nav: {
        organization: {
          label: t('crm.settingsPage.nav.organization.label'),
          description: t('crm.settingsPage.nav.organization.description'),
        },
        modules: {
          label: t('crm.settingsPage.nav.modules.label'),
          description: t('crm.settingsPage.nav.modules.description'),
        },
        packs: {
          label: t('crm.settingsPage.nav.packs.label'),
          description: t('crm.settingsPage.nav.packs.description'),
        },
        objects: {
          label: t('crm.settingsPage.nav.objects.label'),
          description: t('crm.settingsPage.nav.objects.description'),
        },
        customFields: {
          label: t('crm.settingsPage.nav.customFields.label'),
          description: t('crm.settingsPage.nav.customFields.description'),
        },
        developer: {
          label: t('crm.settingsPage.nav.developer.label'),
          description: t('crm.settingsPage.nav.developer.description'),
        },
      },
      organization: {
        title: t('crm.settingsPage.organization.title'),
        description: t('crm.settingsPage.organization.description'),
        noOrg: t('crm.settingsPage.organization.noOrg'),
        fallbackName: t('crm.settingsPage.organization.fallbackName'),
        adminAccess: t('crm.settingsPage.organization.adminAccess'),
        readOnlyAccess: t('crm.settingsPage.organization.readOnlyAccess'),
      },
      roleLanding: {
        title: t('crm.settingsPage.roleLanding.title'),
        description: t('crm.settingsPage.roleLanding.description'),
        defaultStartPoint: t('crm.settingsPage.roleLanding.defaultStartPoint'),
      },
      landingPathOptions: {
        '/dashboard': t('crm.settingsPage.landingPathOptions.dashboard'),
        '/contacts': t('crm.settingsPage.landingPathOptions.contacts'),
        '/companies': t('crm.settingsPage.landingPathOptions.companies'),
        '/deals': t('crm.settingsPage.landingPathOptions.deals'),
        '/pipeline': t('crm.settingsPage.landingPathOptions.pipeline'),
        '/tasks': t('crm.settingsPage.landingPathOptions.tasks'),
        '/inbox': t('crm.settingsPage.landingPathOptions.inbox'),
        '/portal-connections': t('crm.settingsPage.landingPathOptions.portalConnections'),
        '/portal-leads': t('crm.settingsPage.landingPathOptions.portalLeads'),
      },
      summary: {
        organization: t('crm.settingsPage.summary.organization'),
        notSet: t('crm.settingsPage.summary.notSet'),
        yourRole: t('crm.settingsPage.summary.yourRole'),
        moduleAdmin: t('crm.settingsPage.summary.moduleAdmin'),
        yes: t('crm.settingsPage.summary.yes'),
        readOnly: t('crm.settingsPage.summary.readOnly'),
        activeModules: t('crm.settingsPage.summary.activeModules'),
        activeModulesSeparator: t('crm.settingsPage.summary.activeModulesSeparator'),
        bundle: t('crm.settingsPage.summary.bundle'),
        customMix: t('crm.settingsPage.summary.customMix'),
        lastUpdated: t('crm.settingsPage.summary.lastUpdated'),
        defaultConfig: t('crm.settingsPage.summary.defaultConfig'),
      },
      notice: {
        readOnlyTitle: t('crm.settingsPage.notice.readOnlyTitle'),
        readOnlyDescription: t('crm.settingsPage.notice.readOnlyDescription'),
      },
      modules: {
        title: t('crm.settingsPage.modules.title'),
        description: t('crm.settingsPage.modules.description'),
      },
      actions: {
        reset: t('crm.settingsPage.actions.reset'),
        saving: t('crm.settingsPage.actions.saving'),
        saveModules: t('crm.settingsPage.actions.saveModules'),
        discard: t('crm.settingsPage.actions.discard'),
        save: t('crm.settingsPage.actions.save'),
      },
      messages: {
        saved: t('crm.settingsPage.messages.saved'),
        unsavedChanges: t('crm.settingsPage.messages.unsavedChanges'),
      },
      states: {
        loading: t('crm.settingsPage.states.loading'),
        loadFailed: t('crm.settingsPage.states.loadFailed'),
      },
      presetsTitle: t('crm.settingsPage.presetsTitle'),
      presetsDescription: t('crm.settingsPage.presetsDescription'),
      presets: {
        core_crm: {
          label: t('crm.settingsPage.presets.coreCrm.label'),
          description: t('crm.settingsPage.presets.coreCrm.description'),
        },
        sales_only: {
          label: t('crm.settingsPage.presets.salesOnly.label'),
          description: t('crm.settingsPage.presets.salesOnly.description'),
        },
        real_estate: {
          label: t('crm.settingsPage.presets.realEstate.label'),
          description: t('crm.settingsPage.presets.realEstate.description'),
        },
      },
      counts: {
        modules: (count: number) =>
          t('crm.settingsPage.counts.modules', {
            values: { count },
          }),
      },
      statuses: {
        active: t('crm.settingsPage.statuses.active'),
        enabled: t('crm.settingsPage.statuses.enabled'),
        disabled: t('crm.settingsPage.statuses.disabled'),
      },
      collections: {
        shared_core: {
          label: t('crm.settingsPage.collections.sharedCore.label'),
          description: t('crm.settingsPage.collections.sharedCore.description'),
        },
        shared_workflows: {
          label: t('crm.settingsPage.collections.sharedWorkflows.label'),
          description: t('crm.settingsPage.collections.sharedWorkflows.description'),
        },
        real_estate: {
          label: t('crm.settingsPage.collections.realEstate.label'),
          description: t('crm.settingsPage.collections.realEstate.description'),
        },
      },
      collectionKinds: {
        core: t('crm.settingsPage.collectionKinds.core'),
        vertical: t('crm.settingsPage.collectionKinds.vertical'),
      },
      moduleDefinitions: {
        contacts: {
          label: t('crm.settingsPage.moduleDefinitions.contacts.label'),
          description: t('crm.settingsPage.moduleDefinitions.contacts.description'),
        },
        companies: {
          label: t('crm.settingsPage.moduleDefinitions.companies.label'),
          description: t('crm.settingsPage.moduleDefinitions.companies.description'),
        },
        deals: {
          label: t('crm.settingsPage.moduleDefinitions.deals.label'),
          description: t('crm.settingsPage.moduleDefinitions.deals.description'),
        },
        pipeline: {
          label: t('crm.settingsPage.moduleDefinitions.pipeline.label'),
          description: t('crm.settingsPage.moduleDefinitions.pipeline.description'),
        },
        tasks: {
          label: t('crm.settingsPage.moduleDefinitions.tasks.label'),
          description: t('crm.settingsPage.moduleDefinitions.tasks.description'),
        },
        inbox: {
          label: t('crm.settingsPage.moduleDefinitions.inbox.label'),
          description: t('crm.settingsPage.moduleDefinitions.inbox.description'),
        },
        portal_connections: {
          label: t('crm.settingsPage.moduleDefinitions.portalConnections.label'),
          description: t('crm.settingsPage.moduleDefinitions.portalConnections.description'),
        },
        portal_leads: {
          label: t('crm.settingsPage.moduleDefinitions.portalLeads.label'),
          description: t('crm.settingsPage.moduleDefinitions.portalLeads.description'),
        },
      },
      dependencies: {
        requires: (modules: string) =>
          t('crm.settingsPage.dependencies.requires', {
            values: { modules },
          }),
        usedBy: (modules: string) =>
          t('crm.settingsPage.dependencies.usedBy', {
            values: { modules },
          }),
      },
      developer: {
        title: t('crm.settingsPage.developer.title'),
        description: t('crm.settingsPage.developer.description'),
      },
    },
  };
}
